'use client';
import { useEffect, useMemo } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { OT_CONSENT_COOKIE_NAME, isOtPerfEnabled } from '@ab-inbev-eu/utils/onetrust';
import type { Campaign } from '@segment/analytics-core';

import { publicConfig } from '../../utils/config';
import { getCookie } from '../../utils/functions.client';
import useDrag from '../../hooks/useDrag';

import { AnalyticsContext } from './context';

const { SEGMENT, IS_DEV_MODE } = publicConfig();

type Props = React.PropsWithChildren & {
  uid?: string;
};

const enum UtmSearchParams {
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_SOURCE = 'utm_source',
  UTM_MEDIUM = 'utm_medium',
}

const storageEntries = Object.entries({
  name: UtmSearchParams.UTM_CAMPAIGN,
  source: UtmSearchParams.UTM_SOURCE,
  medium: UtmSearchParams.UTM_MEDIUM,
}) as [keyof Campaign, UtmSearchParams][];

export function AnalyticsProvider({ children, uid }: Props) {
  const analytics = useMemo(() => new AnalyticsBrowser(), []);
  const { deltaY, isDragging } = useDrag();

  useEffect(() => {
    const campaignStore = window.sessionStorage.getItem('campaign');
    let campaign: Campaign | null = null;
    if (campaignStore) campaign = JSON.parse(campaignStore) as Campaign;
    const campaignUtmInfo: Partial<Campaign> = campaign ?? {};

    const searchParams = new URLSearchParams(window.location.search);

    storageEntries.forEach(([key, utmName]) => {
      const paramValue = searchParams.get(utmName);
      if (!paramValue) return;
      campaignUtmInfo[key] = paramValue;
    });

    // All three utm params should be present
    if (Object.keys(campaignUtmInfo).length !== storageEntries.length) return;

    window.sessionStorage.setItem('campaign', JSON.stringify(campaignUtmInfo));
  }, []);

  useEffect(() => {
    // Close keyboard when user scrolls up to fix
    //https://stackoverflow.com/questions/75834277/virtual-keyboard-in-safari-causes-black-space-under-app-body-and-scrolling-outsi
    const affectedScrolledHeight = -50;
    if (isDragging && deltaY < affectedScrolledHeight && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [isDragging, deltaY]);

  useEffect(() => {
    if (!SEGMENT.WRITE_KEY || IS_DEV_MODE) return;

    const initSegment = () => {
      if (!isOtPerfEnabled(decodeURIComponent(getCookie(OT_CONSENT_COOKIE_NAME) ?? ''))) return;
      analytics.load(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        { writeKey: SEGMENT.WRITE_KEY! },
        {
          user: {
            cookie: {
              key: uid,
            },
          },
        },
      );
    };

    initSegment();
    window.addEventListener('consent.onetrust', initSegment);
    return () => {
      window.removeEventListener('consent.onetrust', initSegment);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics]);

  return <AnalyticsContext.Provider value={{ analytics }}>{children}</AnalyticsContext.Provider>;
}
